import { usePlatform } from '@/composables/usePlatform';
import type { PermissionCategory } from '@/constants/permissions';
import { useEnvStore } from '@/stores/envStore';
import type { Subscription, User } from '@/types/api';
import type { UserPermission } from '@/types/restaurants';
import { hasSubscriptionFeature } from '@/utils/auth';
import axios from '@/utils/axios';
import { measurePerformance, recordPerformance } from '@/utils/performance';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

export const useUserStore = defineStore('user', () => {
  const CREDIT_WARNING_THRESHOLD = 20;
  const user = ref<User | null>(null);
  const activeRestaurant = ref<User['active_restaurant'] | null>(null);
  const userPermissions = ref<UserPermission[]>([]);
  const lastRestaurantUpdate = ref<Date | null>(null);
  const envStore = useEnvStore();
  const remainingCredits = ref<number>(0);
  const previousCreditsRemaining = ref<number | null>(null);
  const userConfirmedWarning = ref(false);
  const showCreditWarning = ref(false);
  const showNoMoreCreditDialog = ref(false);
  const showSubscriptionChangedDialog = ref(false);
  const showTrialPopup = ref(false);
  const showTrialEndedDialog = ref(false);
  const previousSubscription = ref<Subscription | null>(null);
  const newSubscription = ref<Subscription | null>(null);
  const sessionRefreshPromise = ref<Promise<void> | null>(null);
  const router = useRouter();
  const { makeRequest, isNative } = usePlatform();

  const isPriorityModalActive = computed(() => {
    return (
      showTrialPopup.value ||
      showCreditWarning.value ||
      showNoMoreCreditDialog.value ||
      showSubscriptionChangedDialog.value ||
      showTrialEndedDialog.value
    );
  });

  const hasTeamsFeature = computed(() => {
    return hasSubscriptionFeature(user.value, 'employees_included', 1);
  });

  const showOnboardingDialog = computed(() => {
    // Don't show onboarding if user is on the validate-restaurant page
    const isOnValidateRestaurantPage = router.currentRoute.value.name === 'validate-restaurant';

    return user.value?.onboarding_complete === false && hasTeamsFeature.value && !isPriorityModalActive.value && !isOnValidateRestaurantPage;
  });

  const contextualRole = computed(() => {
    if (user.value?.is_private) {
      return 'single_user';
    }
    return activeRestaurant.value?.role || 'guest';
  });

  // Move subscription logic directly into the store to avoid circular dependency
  const isInTrialPeriod = computed(() => {
    const currentUser = user.value;
    if (!currentUser?.active_restaurant?.trial_ends_at) return false;

    const trialEndDate = new Date(currentUser.active_restaurant.trial_ends_at);
    const now = new Date();
    return trialEndDate > now;
  });

  const hasActiveSubscription = computed(() => {
    const subscription = user.value?.active_restaurant?.subscription;
    return subscription && (['active', 'trialing'].includes(subscription.status) || subscription.is_on_grace_period);
  });

  const isSubscribed = computed(() => {
    return hasActiveSubscription.value || isInTrialPeriod.value;
  });

  const isSessionRefreshInProgress = computed(() => sessionRefreshPromise.value !== null);

  const groupedUserPermissions = computed(() => {
    const grouped: Record<string, string[]> = {};

    userPermissions.value.forEach((permission) => {
      const category = permission.category || 'general';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(permission.name);
    });
    // console.log("grouped permissions: ", grouped)
    return grouped;
  });

  const isEmployee = computed(() => {
    return user.value?.active_restaurant?.role === 'employee';
  });

  function validateUserCanGenerate(creditsNeeded = 1) {
    if (remainingCredits.value < creditsNeeded) {
      console.log('SHOWING NO MORE CREDIT DIALOG');
      useUserStore().showNoMoreCreditDialog = true;
      return false;
    }
    return true;
  }

  // function checkCanUserGenerateDish({ creditsNeeded = 1 }) {
  //   if (remainingCredits.value >= creditsNeeded) {
  //     showNoMoreCreditDialog.value = true
  //     return false
  //   }
  //   return true
  // }

  function hasPermission(category: PermissionCategory, action: string): boolean {
    const permissionName = `${category}_${action}`;

    // Admin bypass
    if (activeRestaurant.value?.role === 'admin' || activeRestaurant.value?.role === 'manager') {
      return true;
    }

    if (!groupedUserPermissions.value || !groupedUserPermissions.value[category]) {
      return false;
    }

    return groupedUserPermissions.value[category].includes(permissionName);
  }

  // Utility function to check multiple permissions (ANY logic)
  function hasAnyPermission(permissions: Array<{ category: PermissionCategory; action: string }>): boolean {
    return permissions.some(({ category, action }) => hasPermission(category, action));
  }

  function saveToLocalStorage() {
    const data = {
      // Only save data that should persist, NOT UI state
      activeRestaurant: activeRestaurant.value,
      userPermissions: userPermissions.value,
      lastRestaurantUpdate: lastRestaurantUpdate.value,
      remainingCredits: remainingCredits.value,
      previousCreditsRemaining: previousCreditsRemaining.value,
      userConfirmedWarning: userConfirmedWarning.value,
      subscription: activeRestaurant.value?.subscription,
      previousSubscription: previousSubscription.value,
      newSubscription: newSubscription.value,
      // Remove UI state from localStorage:
      // showNoMoreCreditDialog: showNoMoreCreditDialog.value,
      // showSubscriptionChangedDialog: showSubscriptionChangedDialog.value,
      // showTrialPopup: showTrialPopup.value,
      // showTrialEndedDialog: showTrialEndedDialog.value,
    };
    localStorage.setItem('restaurantInfoStore', JSON.stringify(data));
  }

  function loadFromLocalStorage() {
    const stored = localStorage.getItem('restaurantInfoStore');
    console.log('Loading user data from localStorage');
    if (stored) {
      // console.log('Stored data found in localStorage:', stored);
      const data = JSON.parse(stored);

      activeRestaurant.value = data.activeRestaurant || null;
      // console.log('Active restaurant loaded from localStorage:', activeRestaurant.value);
      if (activeRestaurant.value) {
        // console.log('Subscription data found:', data.subscription);
        activeRestaurant.value.subscription = data.subscription;
      }
      // console.log('User permissions loaded from localStorage:', data.userPermissions);
      userPermissions.value = data.userPermissions || {};
      lastRestaurantUpdate.value = data.lastRestaurantUpdate ? new Date(data.lastRestaurantUpdate) : null;
      remainingCredits.value = data.remainingCredits || 0;
      previousCreditsRemaining.value = data.previousCreditsRemaining;
      userConfirmedWarning.value = data.userConfirmedWarning || false;
      previousSubscription.value = data.previousSubscription || null;
      newSubscription.value = data.newSubscription || null;

      // Reset all UI state to false when loading from localStorage
      resetUIState();
    }
  }

  function resetUIState() {
    showCreditWarning.value = false;
    showNoMoreCreditDialog.value = false;
    showSubscriptionChangedDialog.value = false;
    showTrialPopup.value = false;
    showTrialEndedDialog.value = false;
  }

  // Add a function to clear user data on logout
  function clearUserData() {
    user.value = null;
    activeRestaurant.value = null;
    userPermissions.value = [];
    resetUIState();
    localStorage.removeItem('restaurantInfoStore');
  }

  async function initUser() {
    resetUIState();
    loadFromLocalStorage();
    const serverUser = await getUser();
    if (serverUser) {
      user.value = serverUser;
      if (serverUser.active_restaurant) {
        activeRestaurant.value = serverUser.active_restaurant;
        userPermissions.value = serverUser.active_restaurant.userPermissions || {};
      }

      // NEW: Check if this is a magic link session on web
      const isMagicLinkSession = !isNative.value && sessionStorage.getItem('magic_link_redirect') !== null;

      // Only show dialogs based on current server state
      // MODIFIED: Don't show trial popup if coming from magic link
      if (isInTrialPeriod.value && !serverUser.trial_popup_accepted && !isMagicLinkSession) {
        showTrialPopup.value = true;
      } else if (!isInTrialPeriod.value && !serverUser.trial_ended_acknowledged && !serverUser.active_restaurant?.subscription) {
        showTrialEndedDialog.value = true;
      }

      // Update credit-related variables
      if (serverUser.active_restaurant) {
        const restaurant = serverUser.active_restaurant;
        const currentCredits = restaurant.total_credits;
        remainingCredits.value = currentCredits;
        previousCreditsRemaining.value = currentCredits;

        const currentSubscription = restaurant.subscription || null;

        // Check if backend indicates a subscription change that hasn't been acknowledged
        if (!serverUser.subscription_changed_acknowledged && currentSubscription) {
          // Only show dialog if we have a stored previous subscription that differs
          // OR if this is flagged by backend as a change (subscription_changed_acknowledged = false)
          const storedPreviousPlanId = previousSubscription.value?.plan?.id;
          const currentPlanId = currentSubscription?.plan?.id;

          // If we have no previous subscription stored but backend says there's an unacknowledged change,
          // it means the subscription just changed and we should show the dialog
          if (storedPreviousPlanId !== currentPlanId || previousSubscription.value === null) {
            // Keep the stored previousSubscription if it exists and differs, otherwise this is a new subscription
            if (previousSubscription.value === null) {
              // First subscription - no "previous" to show, but we should still acknowledge
              // Set previousSubscription to indicate "no previous plan"
              previousSubscription.value = null;
            }
            newSubscription.value = currentSubscription;
            showSubscriptionChangedDialog.value = true;
          }
        } else {
          // Subscription has been acknowledged or no subscription
          // Update the stored previous subscription to current for next comparison
          previousSubscription.value = currentSubscription;
          newSubscription.value = currentSubscription;
        }

        userConfirmedWarning.value = false;
      }
      saveToLocalStorage();
    }
  }

  async function getUser(): Promise<User | null> {
    if (user.value) return user.value;
    try {
      const requestStartedAt = performance.now();
      const response = await measurePerformance('startup:userStore.getUser.request', () => axios.get(envStore.apiUrl + '/me'));
      const userData = response.data as User;
      const serverTiming = response.headers['server-timing'] ?? null;
      recordPerformance('startup:userStore.getUser.payload', JSON.stringify(userData).length / 1024, {
        unit: 'KB',
        route: '/me',
        requestMs: Number((performance.now() - requestStartedAt).toFixed(1)),
        serverTiming,
      });
      // console.log('data from server: ', userData);
      return transformUserData(userData);
    } catch (error) {
      console.error('error', error);
      return null;
    }
  }

  async function refreshUser() {
    if (sessionRefreshPromise.value) {
      await sessionRefreshPromise.value;
      return;
    }

    sessionRefreshPromise.value = (async () => {
      try {
        const requestStartedAt = performance.now();
        const response = await measurePerformance('startup:userStore.refreshUser.request', () => axios.get(envStore.apiUrl + '/me'));
        const userData = response.data as User;
        const serverTiming = response.headers['server-timing'] ?? null;
        recordPerformance('startup:userStore.refreshUser.payload', JSON.stringify(userData).length / 1024, {
          unit: 'KB',
          route: '/me',
          requestMs: Number((performance.now() - requestStartedAt).toFixed(1)),
          serverTiming,
        });
        user.value = transformUserData(userData);

        const oldSubscription = activeRestaurant.value?.subscription;

        // Update active restaurant and permissions
        if (userData.active_restaurant) {
          activeRestaurant.value = userData.active_restaurant;
          userPermissions.value = userData.active_restaurant.userPermissions || {};

          const updatedSubscription = userData.active_restaurant.subscription;

          if (updatedSubscription && !userData.subscription_changed_acknowledged) {
            // console.log('Subscription change detected, showing dialog');
            previousSubscription.value = oldSubscription || null;
            newSubscription.value = updatedSubscription;
            showSubscriptionChangedDialog.value = true;
          }
        }

        saveToLocalStorage();
      } catch (error: unknown) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof (error as { response?: { status?: number } }).response?.status === 'number' &&
          (error as { response?: { status?: number } }).response?.status === 401
        ) {
          // Use clearUserData instead of manual cleanup
          clearUserData();
          // Only redirect to login if the current route requires authentication.
          // Avoids overriding a deep-link navigation to a public route (e.g. reset-password).
          if (router.currentRoute.value.meta.securityConfig?.requireAuthentication) {
            router.push({ name: 'login' });
          }
        }
        console.log('error', error);
      }
    })();

    try {
      await sessionRefreshPromise.value;
    } finally {
      sessionRefreshPromise.value = null;
    }
  }

  function waitForSessionRefresh(): Promise<void> {
    if (sessionRefreshPromise.value) {
      return sessionRefreshPromise.value;
    }
    return Promise.resolve();
  }

  async function ensureFreshSession() {
    await refreshUser();
  }

  const updateSubscription = (newSubscriptionData: Subscription) => {
    console.log('Updating subscription with new data:', newSubscriptionData);
    const oldSubscription = activeRestaurant.value?.subscription;
    const oldSubscriptionName = oldSubscription?.plan?.name;
    const newSubscriptionName = newSubscriptionData?.plan?.name;
    console.log('Updating subscription:', oldSubscriptionName, 'to', newSubscriptionName);

    const isFirstSubscription = !oldSubscription && newSubscriptionData;
    const isPlanChanged = oldSubscription && newSubscriptionData && oldSubscriptionName !== newSubscriptionName;

    if (isFirstSubscription || isPlanChanged) {
      console.log('Subscription changed or first subscription detected.');
      previousSubscription.value = oldSubscription ?? null;
      newSubscription.value = newSubscriptionData;
      showSubscriptionChangedDialog.value = true;
    }

    if (activeRestaurant.value) {
      activeRestaurant.value.subscription = newSubscriptionData;
    }
    saveToLocalStorage();
  };

  const updateCredits = (totalCredits: number) => {
    const currentCredits = totalCredits;

    const monthlyCreditsAllowed = activeRestaurant.value?.subscription?.plan?.features?.ai_generations_per_month || 0;

    const dynamicWarningThreshold = Math.ceil(monthlyCreditsAllowed * 0.1);

    if (currentCredits <= 0) {
      showNoMoreCreditDialog.value = true;
    } else if (
      !userConfirmedWarning.value &&
      (previousCreditsRemaining.value === null || previousCreditsRemaining.value > dynamicWarningThreshold) &&
      currentCredits <= dynamicWarningThreshold &&
      monthlyCreditsAllowed > 0
    ) {
      showCreditWarning.value = true;
    }

    remainingCredits.value = currentCredits;

    if (user.value?.restaurants?.length) {
      user.value.restaurants[0].total_credits = totalCredits;
    }

    previousCreditsRemaining.value = currentCredits;
    saveToLocalStorage();
  };

  function confirmCreditWarning() {
    userConfirmedWarning.value = true;
    showCreditWarning.value = false;
    saveToLocalStorage();
  }

  function confirmNoMoreCreditDialog() {
    showNoMoreCreditDialog.value = false;
    saveToLocalStorage();
  }

  function confirmSubscriptionChange() {
    showSubscriptionChangedDialog.value = false;
    previousSubscription.value = null;
    newSubscription.value = null;
    saveToLocalStorage();
  }

  function transformUserData(userData: User): User {
    console.log('transforming user data');
    return {
      ...userData,
      created_at: new Date(userData.created_at),
      updated_at: new Date(userData.updated_at),
      email_verified_at: userData.email_verified_at ? new Date(userData.email_verified_at) : undefined,
      two_factor_confirmed_at: userData.two_factor_confirmed_at ? new Date(userData.two_factor_confirmed_at) : undefined,
    };
  }

  async function updateRestaurantInfo(forceUpdate = false) {
    // console.log("updating Restaurant Info")
    const now = new Date();
    if (!forceUpdate && lastRestaurantUpdate.value) {
      const minutesSinceLastUpdate = (now.getTime() - lastRestaurantUpdate.value.getTime()) / 60000;
      // console.log("Minutes since last update: ", minutesSinceLastUpdate);
      if (minutesSinceLastUpdate < 10) {
        // Data is fresh, no need to fetch again
        return;
      }
    }

    try {
      if (user.value && user.value.restaurants.length > 0) {
        // console.log("Fetching restaurant info");
        const restaurantId = user.value.restaurants[0].id;
        const requestStartedAt = performance.now();
        const response = await measurePerformance('startup:userStore.updateRestaurantInfo.request', () =>
          axios.get(`${envStore.apiUrl}/restaurants/${restaurantId}`),
        );
        const restaurantData = response.data;
        const serverTiming = response.headers['server-timing'] ?? null;
        recordPerformance('startup:userStore.updateRestaurantInfo.payload', JSON.stringify(restaurantData).length / 1024, {
          unit: 'KB',
          route: `/restaurants/${restaurantId}`,
          requestMs: Number((performance.now() - requestStartedAt).toFixed(1)),
          serverTiming,
        });

        // Update the credits and other necessary fields
        user.value.restaurants[0].total_credits = restaurantData.total_credits;
        // user.value.restaurants[0].credits_used = restaurantData.credits_used;

        // Update the last successful update timestamp
        lastRestaurantUpdate.value = new Date();
      }
    } catch (error) {
      console.error('Failed to update restaurant information:', error);
    }
    if (lastRestaurantUpdate.value) {
      saveToLocalStorage();
    }
    // console.log("DONE");
  }

  async function acceptTrialPopup() {
    try {
      await makeRequest({
        url: `${envStore.apiUrl}/accept-trial-popup`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      showTrialPopup.value = false;
      if (user.value) {
        user.value.trial_popup_accepted = true;
      }
      saveToLocalStorage();
    } catch (error) {
      console.error('Failed to accept trial popup:', error);
    }
  }

  async function acknowledgeSubscriptionChange() {
    try {
      await makeRequest({
        url: `${envStore.apiUrl}/users/acknowledge-subscription-change`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
      showSubscriptionChangedDialog.value = false;
      if (user.value) {
        user.value.subscription_changed_acknowledged = true;
      }
      saveToLocalStorage();
    } catch (error) {
      console.error('Failed to acknowledge subscription change:', error);
    }
  }

  return {
    user,
    activeRestaurant,
    userPermissions,
    groupedUserPermissions,
    contextualRole,
    isEmployee,
    hasPermission,
    hasAnyPermission,
    initUser,
    getUser,
    updateRestaurantInfo,
    updateCredits, // This should now be properly typed
    updateSubscription, // This should now be properly typed
    confirmCreditWarning,
    confirmNoMoreCreditDialog,
    showCreditWarning,
    remainingCredits,
    showNoMoreCreditDialog,
    validateUserCanGenerate,
    refreshUser,
    isSubscribed,
    isSessionRefreshInProgress,
    isInTrialPeriod,
    showSubscriptionChangedDialog,
    showTrialPopup,
    showTrialEndedDialog,
    showOnboardingDialog,
    acceptTrialPopup,
    confirmSubscriptionChange,
    acknowledgeSubscriptionChange,
    previousSubscription,
    newSubscription,
    CREDIT_WARNING_THRESHOLD,
    clearUserData, // Add this to exports
    resetUIState, // Add this to exports
    waitForSessionRefresh,
    ensureFreshSession,
  };
});
